/**
 * 機能の ON/OFF を画面から切り替えるときの判断（2026-09-27 creator/dashboard-review）。FeaturesCard.tsx が使う。
 *
 * WHY 画面から切り替えられるようにしたか（以前は表示だけ）:
 *   クリエイターは管理画面をほぼ開かないが、開いたときに「OFF だから動かない」と分かったなら、その場で直せる方が迷わない。
 *   ただし **正は手元の kumo.config.json**（CLI の `xkumo config push` がファイルの内容で上書きする）。画面だけで切り替えると
 *   次の push で黙って戻る。そこで:
 *     1. 切り替えは必ず確認ダイアログを挟む（何が ON / OFF になるか・どの環境か。本番はプロジェクトの slug を打つ強い確認）
 *     2. 反映したら「手元にも同じ変更を」のコマンド（`xkumo features on …`）をその場で出す
 *     3. この画面で変えたことをブラウザに覚えておき、次に開いたとき「まだ手元に入れていない / その後の push で戻った」を知らせる
 *   サーバーに切り替え専用の API は作らない（既存の PUT …/config = 設定のページと同じ入口・同じ検証・同じ監査ログ）。
 *
 * このファイルは純粋な関数だけ（moneyLogic.ts と同じ理由: `node --test` で型の除去だけで動かす）。
 * import はコマンド名の cliCommand.ts だけ（2026-09-29 creator/xkumo・定数だけのファイル）。拡張子 .ts つきで書く
 * （型の除去だけの node は拡張子を補わない。pendingAction.ts の './launchHidden.ts' と同じ書き方）。
 */

import { CLI_NAME } from './cliCommand.ts';

/** サーバーの GET …/features の 1 行（api.ts の FeatureState と同じ形。import しないためにここで最小限を宣言） */
export interface ToggleFeature {
  key: string;
  configPath: string;
  source: 'features' | 'section' | 'module' | 'api';
  enabled: boolean | null;
  requires?: string[];
}

/**
 * この行を画面のボタンで切り替えられるか。
 *   features.* / ads.enabled / share.<tool>.enabled … 真偽値を 1 つ書き換えるだけなので切り替えられる
 *   ログイン（auth.providers.*）… ON にはクライアント ID・秘密が要る → ログイン方法のページへ案内（'signIn'）
 *   Functions（api）… config ではなく API で ON（資源を作る操作）→ Functions のページへ案内（'functions'）
 */
export type ToggleKind = 'toggle' | 'signIn' | 'functions';
export function toggleKind(f: ToggleFeature): ToggleKind {
  if (f.source === 'api') return 'functions';
  if (f.source === 'module' && f.configPath.startsWith('auth.')) return 'signIn';
  return 'toggle';
}

export interface Change {
  key: string;
  configPath: string;
  on: boolean;
}

/**
 * key を on/off にするときに一緒に変わる機能を含めた変更の一覧（先頭が本人）。
 * WHY 一緒に変えるか: サーバーは依存が欠けた config の push を拒否する（ランキングだけ ON で stats が OFF 等。features.ts の requires）。
 *   黙って片方を変えるのは「勝手に有効になる」になるので、確認ダイアログにこの一覧をそのまま見せて人が選ぶ。
 *   ON … requires を辿って OFF のものを足す / OFF … これに依存していて ON のものも OFF にする（残すと push が拒否される）
 */
export function planToggle(list: ToggleFeature[], key: string, on: boolean): Change[] {
  const byKey = new Map(list.map((f) => [f.key, f]));
  const self = byKey.get(key);
  if (!self) return [];
  const out: Change[] = [{ key, configPath: self.configPath, on }];
  const seen = new Set([key]);
  const queue = [key];
  while (queue.length) {
    const k = queue.shift()!;
    const next = on
      ? (byKey.get(k)?.requires ?? []).map((r) => byKey.get(r)).filter((f): f is ToggleFeature => Boolean(f))
      : list.filter((f) => f.requires?.includes(k));
    for (const f of next) {
      if (seen.has(f.key)) continue;
      seen.add(f.key);
      queue.push(f.key);
      // 既に望む状態のものは変えない（一覧に出さない）が、その先の依存は辿る
      if (Boolean(f.enabled) !== on) out.push({ key: f.key, configPath: f.configPath, on });
    }
  }
  return out;
}

/**
 * config（サーバーが返した正規化済みの全体）に変更を当てた新しいオブジェクト。元は書き換えない。
 * configPath はドット区切り（`features.saves` / `ads.enabled` / `share.images.enabled`）。途中が無ければ作る
 */
export function applyChanges<T>(config: T, changes: Change[]): T {
  const copy = JSON.parse(JSON.stringify(config ?? {})) as Record<string, unknown>;
  for (const c of changes) {
    const parts = c.configPath.split('.');
    let node = copy;
    for (const p of parts.slice(0, -1)) {
      const child = node[p];
      if (!child || typeof child !== 'object' || Array.isArray(child)) node[p] = {};
      node = node[p] as Record<string, unknown>;
    }
    node[parts[parts.length - 1]!] = c.on;
  }
  return copy as T;
}

/**
 * ダッシュボードでは出している（切り替えられる）が、CLI の `features on` がまだ断る機能（2026-10-19 creator/ads-open）。
 * WHY: 広告を作り手に開けるとき、ダッシュボード・SDK・サイトは隠しを外したが、CLI・MCP・Skill は一覧の審査が終わるまで変えない決まり
 *   （docs/teams/creator/tasks/ads-open.md）。CLI の一覧（packages/cli/src/lib/launchHidden.ts）に残る ads・inventory は
 *   `kumodeck features on inventory` が「is not available」で断られる = 「手元にも同じ変更を」にそのコマンドを出すと行き止まり。
 *   そこでこの 2 つはファイルを直す行（manual）にする（`kumodeck config push` は一覧を見ずにファイルをそのまま送るので通る）。
 * 突き合わせ: featureToggle.test.ts が CLI の一覧を読み、「CLI が隠し、ダッシュボードが出す物」とこの一覧が同じことを確かめる
 *   （CLI の審査が済んで一覧から外したら、テストが落ちて「ここからも外す」と分かる）
 */
export const CLI_REFUSED_FEATURES: readonly string[] = ['ads', 'inventory'];

/**
 * 手元の kumo.config.json に同じ変更を入れる方法。
 *   cli … `xkumo features on|off <名前…>` で入る行（features.* と share の道具。CLI の commands/features.ts が share も書く）
 *   manual … CLI では入らず、ファイルを直す行（ads.enabled。CLI は features.ads と書いてしまいサーバーに断られる）・
 *            CLI がまだ断る機能（上の CLI_REFUSED_FEATURES = features.inventory）
 * Kumo のお知らせの欄（kumoNews.optIn・key 'kumoNews'）も CLI で入る（CLI の features on|off kumoNews が kumoNews.optIn を書く・2026-09-27 news-optout）
 */
export function localFix(changes: Change[]): { cli: string[]; manual: Array<{ path: string; on: boolean }> } {
  const cliable = (c: Change) =>
    !CLI_REFUSED_FEATURES.includes(c.key) && (c.configPath.startsWith('features.') || c.configPath.startsWith('share.') || c.configPath === 'kumoNews.optIn');
  const cli: string[] = [];
  for (const on of [true, false]) {
    const names = changes.filter((c) => c.on === on && cliable(c)).map((c) => c.key);
    if (names.length) cli.push(`${CLI_NAME} features ${on ? 'on' : 'off'} ${names.join(' ')}`);
  }
  const manual = changes.filter((c) => !cliable(c)).map((c) => ({ path: c.configPath, on: c.on }));
  return { cli, manual };
}

/** この画面で切り替えた記録（ブラウザの localStorage に project × env ごと）。version = 反映した結果の config の版 */
export interface ToggleRecord {
  version: number;
  at: string;
  changes: Change[];
}

/**
 * 記録と今の状態を比べて、何を知らせるか。
 *   pending  … 画面で変えたあと push が無い（版が同じ）= まだ手元に入れていない可能性。次の push で戻るかもしれない
 *   reverted … その後の push（版が上がった）で、画面で変えた機能が元に戻った（手元のファイルに入っていなかった）
 *   none     … その後の push でも画面で変えた状態のまま = 手元にも入った。記録は消してよい
 * WHY 版で比べるか: 画面で変えた後に誰か（CLI・MCP・CI）が push すると版が上がる。版が同じなら、まだ誰も上書きしていない
 */
export function driftOf(record: ToggleRecord | null, version: number, list: ToggleFeature[]): { kind: 'none' | 'pending' | 'reverted'; changes: Change[] } {
  if (!record || !record.changes.length) return { kind: 'none', changes: [] };
  const state = new Map(list.map((f) => [f.key, f.enabled]));
  // 版が記録より古い = 反映の直後で一覧の読み直しがまだ（ここで none にすると記録を消してしまう）。pending のまま待つ
  if (version <= record.version) return { kind: 'pending', changes: record.changes };
  const back = record.changes.filter((c) => state.has(c.key) && Boolean(state.get(c.key)) !== c.on);
  return back.length ? { kind: 'reverted', changes: back } : { kind: 'none', changes: [] };
}

/** 本番の強い確認: プロジェクトの slug をそのまま打つ（前後の空白は許す） */
export function strongConfirmOk(typed: string, slug: string): boolean {
  return typed.trim() === slug;
}

/**
 * 手元で機能を ON にして反映するコマンド（空の状態・FeatureHint の案内用）。
 * --env は常に付ける: CLI の既定は development なので、本番の画面を見ながら打つと開発環境に push されて「直らない」になる
 */
export function enableCommands(names: string[], env: 'development' | 'production'): string {
  return `${CLI_NAME} features on ${names.join(' ')}\n${CLI_NAME} config push --env ${env}`;
}

/** config を反映するコマンド（環境つき。理由は enableCommands と同じ） */
export function pushCommand(env: 'development' | 'production'): string {
  return `${CLI_NAME} config push --env ${env}`;
}

/**
 * 公開中のサイトを止める OFF（本体の 409 would_stop_serving { features, liveUrl }・Core core/serving-guard・AGT-16 2026-09-29）。
 * 画面の流れ: まず allowTurnOff を付けずに送る（公開中の版が無ければそのまま通る = 聞くことが無い）→ 409 なら、同じダイアログに
 *   「サイトが止まります（URL）」を赤で出し、本人がもう一度押したら `?allowTurnOff=<名前>` を付けて送り直す（CLI の `features off` と同じ流儀）。
 * 付けてよいのは、このダイアログで OFF にする機能（plan）に全部入っているときだけ。409 がそれ以外（手元の config で別に false に
 *   なっていた customDomains など）を挙げたら、それは本人が選んだ切り替えではない = null（付けずに誤りのまま見せる）
 */
export function stopServingAsk(plan: Change[], details: unknown): { features: string[]; liveUrl: string | null } | null {
  const d = (details ?? {}) as { features?: unknown; liveUrl?: unknown };
  const off = new Set(plan.filter((c) => !c.on).map((c) => c.key));
  const features = Array.isArray(d.features) ? d.features.filter((f): f is string => typeof f === 'string') : [];
  if (!features.length || features.some((f) => !off.has(f))) return null;
  return { features, liveUrl: typeof d.liveUrl === 'string' && d.liveUrl ? d.liveUrl : null };
}

/** PUT …/config の path（allowTurnOff があればクエリに。名前は英数字だけだが符号化はする） */
export function configPutPath(base: string, allowTurnOff: string[] | null): string {
  return allowTurnOff?.length ? `${base}/config?allowTurnOff=${allowTurnOff.map(encodeURIComponent).join(',')}` : `${base}/config`;
}
